import { RefObject, useCallback, useEffect, useState } from "react";
import { useDebounce } from "shared/hooks/useDebounce.hook";
import { useResize } from "shared/hooks/useResize.hook";

const loadMoreButtonContainerHeight = 30;

export type UseInfiniteScrollParams<TElement extends HTMLElement> = {
  containerRef?: RefObject<TElement>;
  onReachEndOfList: () => void;
};

export type UseInfiniteScrollReturnType = {
  hasScroll: boolean;
};

export const useInfiniteScroll = <TElement extends HTMLElement>({
  containerRef,
  onReachEndOfList,
}: UseInfiniteScrollParams<TElement>): UseInfiniteScrollReturnType => {
  const [hasScroll, setHasScroll] = useState(false);
  const { debounce } = useDebounce(100);

  const handler = useCallback(() => {
    if (!containerRef?.current) return;
    // TODO: needs to rewrite the infinite scroll logic without using a container
    // for the div (loadMoreBtn, MainView.tsx) and calculating the height at this point in the code
    const scrollHeight = containerRef.current.scrollHeight - loadMoreButtonContainerHeight;
    const scrollTop = containerRef.current.scrollTop;
    const clientHeight = containerRef.current.clientHeight;

    const performNextRequest = scrollHeight - scrollTop <= clientHeight;

    if (performNextRequest) {
      onReachEndOfList?.();
    }
  }, [containerRef, onReachEndOfList]);

  const checkScroll = () => {
    const containerElement = containerRef?.current;
    if (!containerElement) return;
    setHasScroll(containerElement.scrollHeight > containerElement.clientHeight);
  };

  useEffect(() => {
    const element = containerRef?.current;
    element?.addEventListener("scroll", handler);

    return () => {
      element?.removeEventListener("scroll", handler);
    };
  }, [containerRef, handler]);

  useEffect(() => {
    checkScroll();
  }, [containerRef?.current?.childNodes.length]);

  useResize(() =>
    debounce(() => {
      checkScroll();
    })
  );

  return {
    hasScroll,
  };
};
